import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FileText, RefreshCw, Search } from 'lucide-react';
import { EmptyState, TablePagination } from '../ui';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const formatTs = (timestamp) => {
  if (!timestamp) return '—';
  try {
    return new Date(timestamp).toLocaleString('en-US', {
      timeZone: 'Asia/Karachi',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return timestamp;
  }
};

const levelClass = (level) => {
  const l = (level || '').toUpperCase();
  if (l === 'CRITICAL' || l === 'ERROR') {
    return 'text-red-600 dark:text-red-400';
  }
  if (l === 'WARNING') return 'text-amber-600 dark:text-amber-400';
  if (l === 'INFO') return 'text-sky-600 dark:text-sky-400';
  return 'text-secondary-500';
};

const AppLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const [draftSearch, setDraftSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get('/api/monitoring/app-logs', {
        headers: authHeaders(),
        params: {
          page,
          limit,
          ...(level ? { level } : {}),
          ...(search ? { search } : {}),
        },
      });
      setLogs(res.data.logs || []);
      setTotal(res.data.total_count || 0);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load app logs');
    } finally {
      setLoading(false);
    }
  }, [page, limit, level, search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchLogs, 10000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchLogs]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
          <div className="flex-1">
            <label className="text-xs text-secondary-500 mb-1 block">Search message</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                className="input-field pl-9 w-full"
                value={draftSearch}
                onChange={(e) => setDraftSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPage(1);
                    setSearch(draftSearch.trim());
                  }
                }}
                placeholder="Search log messages…"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-secondary-500 mb-1 block">Level</label>
            <select
              className="input-field"
              value={level}
              onChange={(e) => {
                setPage(1);
                setLevel(e.target.value);
              }}
            >
              <option value="">WARNING+</option>
              <option value="WARNING">WARNING</option>
              <option value="ERROR">ERROR</option>
              <option value="CRITICAL">CRITICAL</option>
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-secondary-600 dark:text-secondary-300 pb-2">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-secondary-300"
            />
            Auto-refresh (10s)
          </label>
          <button
            type="button"
            onClick={fetchLogs}
            className="btn-secondary inline-flex items-center gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
        <p className="text-xs text-secondary-500 mt-2">
          Persists WARNING and above to MongoDB (7-day retention). INFO/DEBUG stay in stdout only.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
        {loading && logs.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-secondary-500 gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" /> Loading application logs…
          </div>
        ) : logs.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No application logs yet"
            description="WARNING+ logs from the API process will appear here as they occur."
          />
        ) : (
          <div className="divide-y divide-secondary-100 dark:divide-secondary-700 max-h-[32rem] overflow-y-auto font-mono text-xs">
            {logs.map((log) => (
              <div key={log.id} className="px-4 py-2.5 hover:bg-secondary-50/60 dark:hover:bg-secondary-700/30">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-secondary-400 whitespace-nowrap">{formatTs(log.timestamp)}</span>
                  <span className={`font-semibold w-20 ${levelClass(log.level)}`}>{log.level}</span>
                  <span className="text-secondary-500 truncate max-w-[200px]" title={log.logger_name}>
                    {log.logger_name}
                  </span>
                  {log.correlation_id && (
                    <span className="text-secondary-400 truncate" title={log.correlation_id}>
                      corr:{log.correlation_id.slice(0, 8)}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-secondary-800 dark:text-secondary-200 whitespace-pre-wrap break-words">
                  {log.message}
                </p>
                {log.extra?.exception && (
                  <pre className="mt-2 text-red-600/90 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                    {log.extra.exception}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="px-4 border-t border-secondary-100 dark:border-secondary-700">
          <TablePagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={limit}
            pageSizeOptions={[25, 50, 100, 200]}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setLimit(size);
              setPage(1);
            }}
            itemLabel="logs"
          />
        </div>
      </div>
    </div>
  );
};

export default AppLogsTab;
